import {
  APPLICATION_STATUSES,
  DEAL_UI_STATUS_CODES,
} from "shared/config/constants";

const APPROVED_DEAL_STATUSES = new Set<DEAL_UI_STATUS_CODES>([
  DEAL_UI_STATUS_CODES.approved,
  DEAL_UI_STATUS_CODES.approvedInYardi,
]);

const DECLINED_DEAL_STATUSES = new Set<DEAL_UI_STATUS_CODES>([
  DEAL_UI_STATUS_CODES.declined,
  DEAL_UI_STATUS_CODES.declinedInYardi,
]);

const CANCELLED_DEAL_STATUSES = new Set<DEAL_UI_STATUS_CODES>([
  DEAL_UI_STATUS_CODES.listingClosed,
  DEAL_UI_STATUS_CODES.cancelled,
  DEAL_UI_STATUS_CODES.cancelledInYardi,
]);

/**
 * Canonical wording for the PMS-approved / VERO-not-completed de-confliction
 * (PRODUCT-1522). Defined once here and reused by every consumer (header pill
 * component, list/table labels) so the phrasing never drifts.
 */
export const PMS_APPROVED_LABEL = "Approved in Yardi";
export const VERO_NOT_COMPLETED_LABEL = "Not completed in VERO";
export const PMS_APPROVED_NO_VERO_LABEL = `${PMS_APPROVED_LABEL} - ${VERO_NOT_COMPLETED_LABEL}`;

/**
 * True when a deal is approved on the PMS side (Yardi -> YAP) but no applicant
 * has completed/submitted in VERO. "Completed in VERO" follows the partial-
 * submission rule: at least one applicant submitted, so callers pass
 * `complete > 0` as `hasAnyVeroSubmission`.
 *
 * NOTE (scope): `approvedInYardi` is the only PMS-approval status today, so this
 * de-confliction is intentionally Yardi-only and the user-facing copy hardcodes
 * "Yardi". If another PMS (e.g. Entrata, RealPage) gains an equivalent
 * approved-in-PMS status, extend the status check and make the label dynamic.
 *
 * @param hasAnyVeroSubmission `true` when at least one applicant has submitted in
 *   VERO. This is the single contract every caller computes from its own payload:
 *   the deal-summary path counts `applications[].isSubmitted` while the list/table
 *   path counts `applicantsSummary[].isSubmitted`. Those are 1:1 per deal
 *   (`mapDealToApplicantsSummary` derives applicantsSummary directly from
 *   applications), so the "any submission" predicate is equivalent across paths
 *   and the banner and table badge cannot disagree for the same deal.
 */
export const isPmsApprovedWithoutVeroCompletion = (
  dealUiStatus?: DEAL_UI_STATUS_CODES,
  hasAnyVeroSubmission?: boolean
): boolean =>
  dealUiStatus === DEAL_UI_STATUS_CODES.approvedInYardi &&
  !hasAnyVeroSubmission;

/**
 * Single-string label for list/table contexts. Returns the de-conflicted label
 * when the deal is PMS-approved without VERO completion, otherwise the caller's
 * existing label is preserved unchanged.
 *
 * Gated by the `useYardiApprovedNoVeroStatus` flag (PRODUCT-1522): callers pass
 * the resolved flag value as `enabled`. Defaults to `false` to match the flag's
 * default-off state in `DEFAULT_FLAG_VALUES`, so an omitted argument behaves as
 * flag-off and the fallback label is returned (no user-visible effect).
 */
export const getDeConflictedDealStatusLabel = (
  dealUiStatus: DEAL_UI_STATUS_CODES | undefined,
  hasAnyVeroSubmission: boolean,
  fallbackLabel: string,
  enabled = false
): string =>
  enabled &&
  isPmsApprovedWithoutVeroCompletion(dealUiStatus, hasAnyVeroSubmission)
    ? PMS_APPROVED_NO_VERO_LABEL
    : fallbackLabel;

/**
 * True when an application reflects a *real* applicant submission in VERO.
 *
 * The backend `is_submitted` flag (surfaced as `isSubmitted`) is also true for
 * applications a PMS auto-approved: approving a deal in Yardi flips every
 * application to APPROVED, and `is_submitted == status in {SUBMITTED, APPROVED}`.
 * A genuine VERO submission therefore additionally requires a `submittedAt`
 * timestamp, which is set only by the VERO submit flow. Gating on
 * both keeps the applicant Application/Status pills and the Income/ID cards in
 * agreement with the "Approved in Yardi / Not completed in VERO" header banner.
 */
export const isVeroSubmittedApplication = (application?: {
  isSubmitted?: boolean;
  submittedAt?: string | null;
}): boolean =>
  Boolean(application?.isSubmitted) && Boolean(application?.submittedAt);

export const isApprovedDeal = (dealUiStatus?: DEAL_UI_STATUS_CODES): boolean =>
  APPROVED_DEAL_STATUSES.has(dealUiStatus as DEAL_UI_STATUS_CODES);

export const isDeclinedDeal = (dealUiStatus?: DEAL_UI_STATUS_CODES): boolean =>
  DECLINED_DEAL_STATUSES.has(dealUiStatus as DEAL_UI_STATUS_CODES);

export const isCanceledDeal = (dealUiStatus?: DEAL_UI_STATUS_CODES): boolean =>
  CANCELLED_DEAL_STATUSES.has(dealUiStatus as DEAL_UI_STATUS_CODES);

/**
 * A deal is "decisioned" when a terminal state has been reached:
 * approved, declined, or cancelled (including their Yardi variants and
 * listing closures).
 */
export const isDecisionedDeal = (
  dealUiStatus?: DEAL_UI_STATUS_CODES
): boolean =>
  isApprovedDeal(dealUiStatus) ||
  isDeclinedDeal(dealUiStatus) ||
  isCanceledDeal(dealUiStatus);

interface WithdrawableApplication {
  status?: APPLICATION_STATUSES;
  isSubmitted?: boolean;
  preventEditApplicationAfterSubmit?: boolean;
  dealDecisionIsMade?: boolean;
  dealUiStatus?: DEAL_UI_STATUS_CODES;
}

/**
 * An application is "withdrawable" when the applicant is allowed to pull
 * it back for edits. Mirrors the existing logic in Application.jsx so that
 * the renter-facing Edit Application buttons (both sticky and footer) and
 * the list-page View/Edit label stay in sync.
 */
export const isApplicationWithdrawable = (
  application: WithdrawableApplication | null | undefined
): boolean => {
  if (!application) return false;

  const isSubmitted =
    application.isSubmitted !== undefined
      ? application.isSubmitted === true
      : application.status === APPLICATION_STATUSES.submitted;

  if (!isSubmitted) return false;
  if (application.preventEditApplicationAfterSubmit) return false;
  if (application.dealDecisionIsMade) return false;
  if (isDecisionedDeal(application.dealUiStatus)) return false;

  return true;
};
